import {
  createEntityAdapter,
  type EntityAdapter,
  type EntityState,
} from '@ngrx/entity';
import { type Action, createReducer, on } from '@ngrx/store';

import * as CdwBulkUpdateActionActions from './cdw-bulk-update-action.actions';
import type { CdwBulkUpdateActionEntity } from './cdw-bulk-update-action.models';

export const CDW_BULK_UPDATE_ACTION_FEATURE_KEY = 'cdwBulkUpdateAction';

export interface CdwBulkUpdateActionsState
  extends EntityState<CdwBulkUpdateActionEntity> {
  selectedId?: string | number; // which CdwBulkUpdateAction record has been selected
  loaded: boolean; // has the CdwBulkUpdateActions list been loaded
  error?: string | null; // last known error (if any)
}

export interface CdwBulkUpdateActionPartialState {
  readonly [CDW_BULK_UPDATE_ACTION_FEATURE_KEY]: CdwBulkUpdateActionsState;
}

export const cdwBulkUpdateActionAdapter: EntityAdapter<CdwBulkUpdateActionEntity> =
  createEntityAdapter<CdwBulkUpdateActionEntity>();

export const initialCdwBulkUpdateActionState: CdwBulkUpdateActionsState =
  cdwBulkUpdateActionAdapter.getInitialState({
    // set initial required properties
    loaded: false,
  });

const createCdwBulkUpdateActionReducer = createReducer(
  initialCdwBulkUpdateActionState,
  on(
    CdwBulkUpdateActionActions.loadCdwBulkUpdateActionsSuccess,
    (state, { cdwBulkUpdateActions }) =>
      cdwBulkUpdateActionAdapter.setAll(cdwBulkUpdateActions, {
        ...state,
        loaded: true,
      })
  ),
  on(
    CdwBulkUpdateActionActions.addBulkUpdateAction,
    (state, cdwBulkUpdateActionEntity) =>
      cdwBulkUpdateActionAdapter.addOne(cdwBulkUpdateActionEntity, state)
  ),
  on(CdwBulkUpdateActionActions.removeBulkUpdateAction, (state, { id }) =>
    cdwBulkUpdateActionAdapter.removeOne(id, state)
  ),
  on(
    CdwBulkUpdateActionActions.disableBulkUpdateActionNextbutton,
    (state, { id }) =>
      cdwBulkUpdateActionAdapter.updateOne(
        { id, changes: { isNextButtonDisabled: true } },
        state
      )
  ),
  on(
    CdwBulkUpdateActionActions.enableBulkUpdateActionNextbutton,
    (state, { id }) =>
      cdwBulkUpdateActionAdapter.updateOne(
        { id, changes: { isNextButtonDisabled: false } },
        state
      )
  ),
  on(
    CdwBulkUpdateActionActions.displayCancellingBulkUpdateAction,
    (state, { id }) =>
      cdwBulkUpdateActionAdapter.updateOne(
        { id, changes: { isCancellingJob: true } },
        state
      )
  ),
  on(
    CdwBulkUpdateActionActions.hideCancellingBulkUpdateAction,
    (state, { id }) =>
      cdwBulkUpdateActionAdapter.updateOne(
        { id, changes: { isCancellingJob: false } },
        state
      )
  ),
  on(
    CdwBulkUpdateActionActions.getBulkUpdateActionJob,
    (state): CdwBulkUpdateActionsState => ({
      ...state,
    })
  ),
  on(
    CdwBulkUpdateActionActions.getBulkUpdateActionSuccess,
    (state, { id, job }) =>
      cdwBulkUpdateActionAdapter.updateOne(
        { id, changes: { bulkUpdateActionJob: job } },
        state
      )
  ),
  on(
    CdwBulkUpdateActionActions.getFieldUpdatesSummary,
    (state): CdwBulkUpdateActionsState => ({
      ...state,
    })
  ),
  on(
    CdwBulkUpdateActionActions.getFieldUpdatesSummarySuccess,
    (state, { id, summary }) =>
      cdwBulkUpdateActionAdapter.updateOne(
        { id, changes: { fieldUpdatesSummary: summary } },
        state
      )
  )
);

export function cdwBulkUpdateActionReducer(
  state: CdwBulkUpdateActionsState | undefined,
  action: Action
) {
  return createCdwBulkUpdateActionReducer(state, action);
}
